import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { Observable, defer, firstValueFrom } from 'rxjs';

import { retryTransientHttp } from '@core/shared/util';
import { AuthConfig, ChangePassword } from '@mp/auth/common/domain';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly oauthService = inject(OAuthService);
  private readonly http = inject(HttpClient);

  private readonly _apiBasePath: string = 'api/auth';

  /**
   * Configures the login and executes it.
   * @param authConfig OAuth configuration.
   * @param state A state object that is preserved during the login process.
   */
  async setupAuthAndLogin<TState>(authConfig: AuthConfig, state: TState): Promise<TState> {
    this.setupAuth(authConfig);

    return await this.executeCodeFlowWithState(state);
  }

  getAccessToken(): string | null {
    if (this.oauthService.hasValidAccessToken()) {
      return this.oauthService.getAccessToken();
    }
    return null;
  }

  logout(noRedirectToLogoutUrl = false): void {
    this.oauthService.logOut(noRedirectToLogoutUrl);
  }

  changePassword(dto: ChangePassword): Observable<void> {
    return this.http.post<void>(`${this._apiBasePath}/changepassword`, dto);
  }

  private setupAuth(authConfig: AuthConfig): void {
    this.oauthService.configure(authConfig);
    this.oauthService.setupAutomaticSilentRefresh();
  }

  private async executeCodeFlowWithState<TState>(state: TState): Promise<TState> {
    await this.loadDiscoveryDocumentWithRetry();

    /**
     * Bewusst ohne Wiederholung: der Authorization Code ist einmalig einlösbar. Ein zweiter Versuch würde
     * denselben Code erneut senden und vom Identity-Server korrekt abgelehnt werden.
     */
    await this.oauthService.tryLoginCodeFlow();

    // Wrap this, because the if branch should not fulfill. It redirects to the login page.
    return new Promise((resolve) => {
      if (!this.oauthService.hasValidAccessToken()) {
        // Note: This will actually **exit** the app if no token is present
        this.oauthService.initCodeFlow(state === undefined ? undefined : JSON.stringify(state));
        return;
      }

      resolve(this.oauthService.state ? (JSON.parse(decodeURIComponent(this.oauthService.state)) as TState) : state);
    });
  }

  /**
   * Holt das Discovery-Dokument (reiner GET) und wiederholt es bei vorübergehenden Fehlern, damit ein
   * Kaltstart die App nicht abbricht. `defer` löst den Aufruf pro Versuch neu aus. Deterministische
   * Ablehnungen der Bibliothek (unsicherer Issuer, ungültiges Discovery-Dokument) sind keine HTTP-Fehler und
   * scheitern deshalb sofort.
   */
  private async loadDiscoveryDocumentWithRetry(): Promise<void> {
    await firstValueFrom(defer(() => this.oauthService.loadDiscoveryDocument()).pipe(retryTransientHttp()));
  }
}
